import type {
  AysCodeResponse,
  AysInekInlierType,
} from '@bindoc-projects/code-domain-lib';
import {
  AysCode
} from '@bindoc-projects/code-domain-lib';
import { BdAdministrativeDistrictId } from '@bindoc-projects/geographic-domain-lib';
import type { BdVirtualClinicInsuranceType } from './insurance-type';
import type { BdPatientClinicalComplexityLevel } from '../pccl';
import type { ChargeRealm } from './charge-realm';

export class AysFullyInpatientCase {
  static fromObjArr(objArr: any[]): AysFullyInpatientCase[] {
    const mapFn = (value: any) => AysFullyInpatientCase.fromObj(value);

    return objArr.map(mapFn);
  }

  static fromObj(obj: any): AysFullyInpatientCase {
    const code = obj.code ? AysCode.fromObject(obj.code) : null;
    const additionalInformation =
      AysFullyInpatientCaseAdditionalInformation.fromObj(
        obj.additionalInformation,
      );

    return new AysFullyInpatientCase(
      obj.caseId,
      obj.internMark,
      additionalInformation,
      obj.amount,
      obj.casemixEffektiv,
      obj.casemixRelevantAmount,
      code,
      obj.count,
      obj.fabKey,
      obj.specialtyDepartmentId,
      obj.virtualClinicId,
      obj.year,
      AysFullyInpatientCaseProcedure.fromObjArray(obj.procedures),
      AysFullyInpatientCaseDiagnose.fromObjectArray(obj.diagnoses),
      obj.mainDiagnosis ? AysCode.fromObject(obj.mainDiagnosis) : undefined,
      obj.mainProcedure ? AysCode.fromObject(obj.mainProcedure) : undefined,
      AysFullyInpatientCaseCharge.fromObjectArray(obj.charges),
      obj.chargeRealm,
    );
  }

  constructor(
    public caseId: string,
    public internMark: string,
    public additionalInformation: AysFullyInpatientCaseAdditionalInformation,
    public amount: number,
    public casemixEffektiv: number,
    public casemixRelevantAmount: number,
    public code: AysCode,
    public count: number,
    public fabKey: string,
    public specialtyDepartmentId: string,
    public virtualClinicId: string,
    public year: number,
    public procedures: AysFullyInpatientCaseProcedure[],
    public diagnoses: AysFullyInpatientCaseDiagnose[],
    public mainDiagnosis?: AysCode,
    public mainProcedure?: AysCode,
    public charges: AysFullyInpatientCaseCharge[] = [],
    public chargeRealm: ChargeRealm | null = null,
  ) {}
}

export class AysFullyInpatientCaseAdditionalInformation {
  constructor(
    public administrativeDistrictId: BdAdministrativeDistrictId,
    public postalCode: string,
    public fabs: AysFullyInpatientCaseAdditionalInformationFab[],
    public admission: AysFullyInpatientCaseAdditionalInformationAdmission,
    public dismissal: AysFullyInpatientCaseAdditionalInformationDismissal,
    public patientInformation: {
      agmId: number;
      gender: string;
      insuredPersonId: null;
    },
    public caseInformation: {
      artificialRespirationHours: number;
      caseJunction: boolean;
      caseJunctionReason: string;
      daysOtherChargeRealm: number;
      idSpecialCase: string;
      inlierType: AysInekInlierType;
      insuranceType: BdVirtualClinicInsuranceType;
      intercurrentDialysis: string;
      leaveAbsenceDaysPsy: string;
      lengthOfStayIntensiveCareUnit: number;
      p64b: any;
      pccl: BdPatientClinicalComplexityLevel;
      postdischargeTreatmentDays: number;
      postdischargeTreatmentEnd: string;
      preadmissionTreatmentDays: string;
      preadmissionTreatmentStart: string;
      recordingSeverity: string;
    },
  ) {}

  public static fromObj(obj: {
    administrativeDistrictId: number;
    admission: {
      cause: string;
      date: string;
      reason: string;
    };
    dismissalLocation: any;
    dismissal: {
      date: string;
      reason: string;
    };
    postalCode: string;
    fabs: {
      admissionDate: string;
      dismissalDate: string;
      fabIntern: any;
      fabKey: string;
      fabType: any;
      intensiveCareBed: boolean;
      station: any;
    }[];
    caseInformation: {
      artificialRespirationHours: number;
      caseJunction: boolean;
      caseJunctionReason: string;
      daysOtherChargeRealm: number;
      idSpecialCase: string;
      inlierType: AysInekInlierType;
      insuranceType: BdVirtualClinicInsuranceType;
      intercurrentDialysis: string;
      leaveAbsenceDaysPsy: string;
      lengthOfStayIntensiveCareUnit: number;
      p64b: any;
      pccl: BdPatientClinicalComplexityLevel;
      postdischargeTreatmentDays: number;
      postdischargeTreatmentEnd: string;
      preadmissionTreatmentDays: string;
      preadmissionTreatmentStart: string;
      recordingSeverity: string;
    };
    patientInformation: { agmId: 1; gender: null; insuredPersonId: null };
  }): AysFullyInpatientCaseAdditionalInformation {
    const administrativeDistrictId = obj.administrativeDistrictId
      ? BdAdministrativeDistrictId.createFromNumber(
          obj.administrativeDistrictId,
        )
      : null;

    return new AysFullyInpatientCaseAdditionalInformation(
      administrativeDistrictId,
      obj.postalCode,
      obj.fabs.map((it) => ({
        ...it,
        admissionDate: new Date(it.admissionDate),
        dismissalDate: new Date(it.dismissalDate),
      })),
      {
        ...obj.admission,
        date: new Date(obj.admission.date),
      },
      {
        ...obj.dismissal,
        date: new Date(obj.dismissal.date),
      },
      obj.patientInformation,
      obj.caseInformation,
    );
  }
}

export interface AysFullyInpatientCaseAdditionalInformationAdmission {
  cause: string;
  date: Date;
  reason: string;
}

export interface AysFullyInpatientCaseAdditionalInformationDismissal {
  date: Date;
  reason: string;
}

export interface AysFullyInpatientCaseAdditionalInformationFab {
  admissionDate: Date;
  dismissalDate: Date;
  fabKey: string;
  intensiveCareBed: boolean;
}

export class AysFullyInpatientCaseDiagnose {
  public static fromObjectArray(obj: any[]): AysFullyInpatientCaseDiagnose[] {
    return obj.map((it) => AysFullyInpatientCaseDiagnose.fromObject(it));
  }

  public static fromObject(obj: {
    count: number;
    diagnoseType: 'ND' | 'HD';
    chargeRealm: 'DRG' | string;
    version: string;
    diagnosisSecurity: any;
    localisation: string;
    skCode?: AysCodeResponse;
    skLocalisation?: string;
    skDiagnosisSecurity?: any;
    code: AysCodeResponse;
  }): AysFullyInpatientCaseDiagnose {
    return new AysFullyInpatientCaseDiagnose(
      obj.count,
      obj.diagnoseType,
      obj.chargeRealm,
      obj.version,
      AysCode.fromObject(obj.code),
      obj.localisation,
      obj.diagnosisSecurity,
      obj.skCode ? AysCode.fromObject(obj.skCode) : undefined,
      obj.skLocalisation,
      obj.skDiagnosisSecurity,
    );
  }

  constructor(
    public count: number,
    public diagnoseType: 'ND' | 'HD',
    public chargeRealm: 'DRG' | string,
    public version: string,
    public code: AysCode,
    public localisation: string,
    public diagnosisSecurity: any,
    public skCode?: AysCode,
    public skLocalisation?: string,
    public skDiagnosisSecurity?: any,
  ) {}
}

export class AysFullyInpatientCaseProcedure {
  public static fromObjArray(obj: any[]): AysFullyInpatientCaseProcedure[] {
    return obj.map((it) => AysFullyInpatientCaseProcedure.fromObj(it));
  }

  public static fromObj(obj: {
    count: number;
    procedureDate: string;
    localisation: string;
    code: AysCodeResponse;
  }): AysFullyInpatientCaseProcedure {
    return new AysFullyInpatientCaseProcedure(
      obj.count,
      new Date(obj.procedureDate),
      obj.localisation,
      AysCode.fromObject(obj.code),
    );
  }

  constructor(
    public count: number,
    public procedureDate: Date,
    public localisation: string,
    public code: AysCode,
  ) {}
}

export class AysFullyInpatientCaseCharge {
  public static fromObjectArray(obj: any[]): AysFullyInpatientCaseCharge[] {
    return obj.map((it) => AysFullyInpatientCaseCharge.fromObject(it));
  }

  public static fromObject(obj: {
    accountingEnd: string | null;
    accountingStart: string | null;
    amount: number;
    chargeRealm: 'DRG';
    count: number;
    dayOfTreatment: any;
    daysWithoutCharge: number;
    type: string;
  }): AysFullyInpatientCaseCharge {
    return new AysFullyInpatientCaseCharge(
      obj.accountingStart ? new Date(obj.accountingStart) : undefined,
      obj.accountingEnd ? new Date(obj.accountingEnd) : undefined,
      obj.amount,
      obj.chargeRealm,
      obj.count,
      obj.dayOfTreatment,
      obj.daysWithoutCharge,
      obj.type,
    );
  }

  constructor(
    public accountingStart: Date | undefined,
    public accountingEnd: Date | undefined,
    public amount: number,
    public chargeRealm: 'DRG',
    public count: number,
    public dayOfTreatment: any,
    public daysWithoutCharge: number,
    public type: string,
  ) {}

  public typeDescription(): string {
    switch (this.type.slice(0, 2)) {
      case '01':
        return 'Tagesgleicher Pflegesatz für Allgemeine Psychiatrie, Kinder- und Jugendpsychiatrie, und Psychosomatik/Psychotherapie';
      case '02':
        return 'Ermäßigter Abteilungspflegesatz für Allgemeine Psychiatrie, Kinder- und Jugendpsychiatrie, und Psychosomatik/Psychotherapie nach § 14 Abs. 2 Satz 3 oder Abs. 7 Satz 2 BPflV';
      case '40':
        return 'Zuschlag nach § 8 Abs. 3 BPflV bzw. § 8 Abs. 3 KHEntgG (Investitionszuschlag)';
      case '41':
        return 'Entgelt für vorstationäre Behandlung';
      case '42':
        return 'Entgelt für nachstationäre Behandlung';
      case '43':
        return 'Pflegesatz bei Beurlaubung';
      case '44':
        return 'Modellvorhaben nach § 24 BPflV bzw. § 26 BPflV';
      case '45':
        return 'Wahlleistung Unterkunft (nur für Knappschaft)';
      case '46':
        return 'Zuschlag für Qualitätssicherung nach § 7 Absatz 1 Satz 1 Nr. 7 KHEntgG oder § 7 Satz 1 Nr.3 BPflV';
      case '47':
        return 'Zu-und Abschlag nach § 7 Abs. 1 Satz 1 Nr. 4 KHEntgG bzw. § 7 Satz 1 Nr. 3 und Satz 2 BPflV und sonstiger Zu- und Abschlag';
      case '48':
        return 'DRG Systemzuschlag';
      case '49':
        return 'Abrechnungsergänzungen';
      case '60':
        return 'Sonderfall';
      case '61':
        return 'Entgelt für integrierte Versorgung nach § 140c SGB V';
      case '62':
        return 'Abschlag bei Entgelten für integrierte Versorgung nach § 140c SGB V';
      case '63':
        return 'Entgelt für Modellvorhaben nach § 63 SGB V';
      case '65':
        return 'Zusatzentgelt für DMP';
      case '70':
        return 'DRG-Fallpauschale nach § 7 Abs. 1 Satz 1 Nr. 1 KHEntgG';
      case '71':
        return 'Entgelt bei Überschreiten der oberen GVD nach § 1 Abs. 2 Satz 1 FPV oder tagesbezogene teilstationäre DRG-Fallpauschale ab 2. Tag';
      case '72':
        return 'Abschlag bei Verlegungen nach § 1 Abs. 1 Satz 3 FPV';
      case '73':
        return 'Abschlag bei Nichterreichen der unteren GVD nach § 1 Abs. 3 Satz 1 FPV';
      case '74':
        return 'Entgelt für Pflegeerlös/Tag';
      case '75':
        return 'Zu- und Abschlag nach § 7 Abs. 1 Satz 1 Nr. 4 KHEntgG';
      case '76':
        return 'Zusatzentgelt nach § 7 Abs. 1 Satz 1 Nr. 2, 3, 5 oder 6 KHEntgG';
      case '78':
        return 'Teilstationäre Leistung nach § 6 Abs. 2 FPV';
      case '80':
        return 'Entgelt für neue Untersuchungs- und Behandlungsmethoden nach § 6 Abs. 2 KHEntgG (fallbezogen)';
      case '81':
        return 'Entgelt bei Überschreiten der oberen GVD für fallbezogene Entgelte nach § 6 Abs. 2 KHEntgG';
      case '82':
        return 'Abschlag bei Verlegung für fallbezogene Entgelte nach § 6 Abs. 2 KHEntgG';
      case '83':
        return 'Abschlag bei Nichterreichen der unteren GVD für fallbezogene Entgelte nach § 6 Abs. 2 KHEntgG';
      case '84':
        return 'Pflegeanteil für tages- oder fallbezogenes Entgelt nach § 7 Abs. 1 Satz 1 Nr. 5 KHEntgG';
      case '85':
        return 'Tagesbezogenes Entgelt nach § 7 Abs. 1 Satz 1 Nr. 5 KHEntgG';
      case '86':
        return 'Fallbezogenes Entgelt nach § 7 Abs.1 Satz 1 Nr. 5 KHEntgG';
      case '87':
        return 'Entgelt bei Überschreiten der oberen GVD für fallbezogen Entgelt nach § 7 Abs. 1 Satz 1 Nr.5 KHEntgG';
      case '88':
        return 'Abschlag bei Verlegung für fallbezogene Entgelte nach § 7 Abs. 1 Satz 1 Nr. 5 KHEntgG';
      case '89':
        return 'Abschlag bei Nichterreichen der unteren GVD für fallbezogene Entgelte nach § 7 Abs. 1 Satz 1 Nr. 5 KHEntgG';
      case '90':
        return 'Qualitätsverträge nach § 110a SGB V';
      case '91':
        return 'Übergangspflege';
      default:
        return '';
    }
  }
}
